import React from 'react';
import { createPortal } from 'react-dom';
import { getFloatingMenuStyle, type FloatingMenuAlign } from '../../lib/floatingMenu';

export const DropdownCaretIcon: React.FC<React.SVGProps<SVGSVGElement>> = React.memo(
  function DropdownCaretIcon(props) {
    return (
      <svg
        {...props}
        viewBox="0 0 16 16"
        width="12"
        height="12"
        aria-hidden="true"
        focusable="false"
        style={{ display: 'block', fill: 'currentColor', ...(props.style || {}) }}
      >
        <path d="M3.47 6.22a.75.75 0 0 1 1.06 0L8 9.69l3.47-3.47a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 0 1 0-1.06Z" />
      </svg>
    );
  },
);
DropdownCaretIcon.displayName = 'DropdownCaretIcon';

export type SplitActionItem = {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  title?: string;
  /** Optional decorative icon before the label. */
  icon?: React.ReactNode;
  /** Optional second line under the label. */
  description?: string;
  /** A step that ends something (Lost, Cancel): red. */
  danger?: boolean;
  /** Draw a line above this item, setting it apart from the ones before. */
  separatorBefore?: boolean;
};

type SplitActionButtonProps = {
  /** The everyday action, run by clicking the wide half. */
  label: React.ReactNode;
  onClick: () => void;
  /** The rest of the actions, behind the caret. An empty list hides the caret. */
  items: SplitActionItem[];
  disabled?: boolean;
  /** Disables only the wide half, so the caret still opens the menu (a stage you wait on, with steps behind it). */
  primaryDisabled?: boolean;
  title?: string;
  /** Extra classes for the primary half, e.g. `btn-primary`. */
  className?: string;
  /** Shared size class for both halves, matching the surrounding toolbar. */
  size?: 'xs' | 'sm' | '';
  menuLabel?: string;
  menuAlign?: FloatingMenuAlign;
  menuMinWidth?: number;
  /** id of a hint that explains the buttons, e.g. why they are disabled; set on both halves. */
  describedBy?: string;
};

/**
 * One primary action with the rest of its family behind a caret.
 *
 * A toolbar that grows past three or four buttons stops reading as a set of
 * choices and starts reading as clutter, so the common action keeps its button
 * and everything else moves one click away. Lifted from the order workspace's
 * search control so both surfaces share one behaviour rather than two
 * near-copies.
 *
 * The menu is portalled to the body — a toolbar that scrolls or clips would
 * otherwise cut it off — which is why it is positioned against the caret by
 * hand.
 */
export default function SplitActionButton({
  label,
  onClick,
  items,
  disabled = false,
  primaryDisabled = false,
  title,
  className = '',
  size = '',
  menuLabel = 'More actions',
  menuAlign = 'left',
  menuMinWidth = 220,
  describedBy,
}: SplitActionButtonProps) {
  const [open, setOpen] = React.useState(false);
  const caretRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const firstItemRef = React.useRef<HTMLButtonElement | null>(null);
  // Only a keyboard open moves focus into the menu; a mouse user's pointer is
  // already where they want it.
  const openedByKeyboard = React.useRef(false);
  const enabledItems = items.filter((item) => !item.disabled);
  const hasMenu = items.length > 0;
  const sizeClass = size ? ` ${size}` : '';

  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (caretRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  React.useEffect(() => {
    if (!open || !openedByKeyboard.current) return;
    openedByKeyboard.current = false;
    window.requestAnimationFrame(() => firstItemRef.current?.focus());
  }, [open]);

  const closeAndRestoreFocus = React.useCallback(() => {
    setOpen(false);
    caretRef.current?.focus();
  }, []);

  const handleMenuKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // The open menu consumes Escape, so a page or drawer Escape shortcut does not also fire.
      event.preventDefault();
      event.stopPropagation();
      closeAndRestoreFocus();
      return;
    }
    const nodes = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (nodes.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const activeIndex = nodes.findIndex((node) => node === document.activeElement);
      const fallbackIndex = direction === 1 ? 0 : nodes.length - 1;
      const nextIndex = activeIndex === -1
        ? fallbackIndex
        : (activeIndex + direction + nodes.length) % nodes.length;
      nodes[nextIndex]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      nodes[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      nodes[nodes.length - 1]?.focus();
    }
  }, [closeAndRestoreFocus]);

  const handleCaretKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openedByKeyboard.current = true;
      setOpen(true);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (open) event.stopPropagation();
      setOpen(false);
    }
  }, [open]);

  return (
    <div
      className="split-action-button"
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch' }}
    >
      <button
        type="button"
        className={`btn${sizeClass}${className ? ` ${className}` : ''}`}
        onClick={() => {
          setOpen(false);
          onClick();
        }}
        disabled={disabled || primaryDisabled}
        title={title}
        aria-describedby={describedBy}
        style={hasMenu ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : undefined}
      >
        {label}
      </button>
      {hasMenu ? (
        <>
          <button
            ref={caretRef}
            type="button"
            className={`btn${sizeClass}${className ? ` ${className}` : ''}`}
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : 'false'}
            aria-label={menuLabel}
            aria-describedby={describedBy}
            title={menuLabel}
            // The caret only opens its menu, so a record header's phone sheet (RecordHeader) stays open for it.
            data-rh-menu-keep-open=""
            disabled={disabled || enabledItems.length === 0}
            onClick={() => {
              openedByKeyboard.current = false;
              setOpen((current) => !current);
            }}
            onKeyDown={handleCaretKeyDown}
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              marginLeft: -1,
              padding: '0 0.5rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DropdownCaretIcon />
          </button>
          {open && typeof document !== 'undefined' ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="card"
              onKeyDown={handleMenuKeyDown}
              style={{
                ...getFloatingMenuStyle(caretRef.current, menuMinWidth, menuAlign),
                padding: '0.5rem',
                display: 'grid',
                gap: '0.25rem',
              }}
            >
              {items.map((item, index) => (
                <React.Fragment key={item.label}>
                  {item.separatorBefore ? <div role="separator" className="split-action-button__separator" /> : null}
                  <button
                    ref={index === items.findIndex((candidate) => !candidate.disabled)
                      ? firstItemRef
                      : undefined}
                    type="button"
                    role="menuitem"
                    className={`dropdown-menu__item${item.danger ? ' dropdown-menu__item--danger' : ''}`}
                    disabled={item.disabled}
                    title={item.title}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect();
                    }}
                  >
                    {item.icon ? <span className="dropdown-menu__item-icon" aria-hidden="true">{item.icon}</span> : null}
                    {item.description ? (
                      <span className="dropdown-menu__item-text">
                        <span>{item.label}</span>
                        <small>{item.description}</small>
                      </span>
                    ) : item.label}
                  </button>
                </React.Fragment>
              ))}
            </div>,
            document.body,
          ) : null}
        </>
      ) : null}
    </div>
  );
}
